import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import { Sesame } from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { Box } from '@rebass/grid';
import { gql } from '@apollo/client';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { ReviewModal_Ticket_Fragment, ReviewModal_Booking_Fragment } from '../generated/graphql';
import ContentWrapper from './ContentWrapper';
import LeaveBookingReview from './LeaveBookingReview';
import { wrapperPadding } from './Page';
import ReadReview from './ReadReview';
import LeaveActivityReview from './LeaveActivityReview';
import { defineMessages, IntlShape } from 'react-intl';
import { normalizeReviewData } from '../utils/normalizeReviewData';

export const query = gql`
    fragment ReviewModal on OrderItem {
        ... on Booking {
            id
            review {
                ...Review
            }
            rentalUnit {
                id
                flexCancellationDays
                reviewStats {
                    ...ReviewStats
                }
                accommodation {
                    id
                    ownerName
                    jpegOwner: ownerImage {
                        transform(config: AVATAR, format: JPEG) {
                            ...Transform
                        }
                    }
                    webpOwner: ownerImage {
                        transform(config: AVATAR, format: WEBP) {
                            ...Transform
                        }
                    }
                }
            }
        }
        ... on Ticket {
            id
            activityReview {
                ...ActivityReview
            }
            activity {
                activityReviewStats {
                    ...ActivityReviewStats
                }
            }
        }
        ...OrderSummaryItem
    }

    mutation LeaveReview($input: LeaveReviewInput!) {
        leaveReview(input: $input) {
            review {
                ...Review
            }
        }
    }

    mutation LeaveActivityReview($input: LeaveActivityReviewInput!) {
        leaveActivityReview(input: $input) {
            activityReview {
                ...ActivityReview
            }
        }
    }

    fragment Review on Review {
        id
        average
        comment
        date
        ratingHospitality
        ratingLayout
        ratingLocation
        ratingMaintenance
        ratingValueForMoney
        tips
        title
        travelGroupType
        reply
    }

    fragment ActivityReview on ActivityReview {
        id
        title
        average
        comment
        date
        ratingHospitality
        ratingActivity
        ratingValueForMoney
        tips
        reply
    }
`;

export interface ReviewModalProps extends Pick<Sesame, 'onClose' | 'open'> {
    data: ReviewModal_Booking_Fragment | ReviewModal_Ticket_Fragment;
}

const reviewMessages = defineMessages({
    '10': {
        defaultMessage: '10. Uitmuntend',
    },
    '9': {
        defaultMessage: '9. Fantastisch',
    },
    '8': {
        defaultMessage: '8. Erg goed',
    },
    '7': {
        defaultMessage: '7. Goed',
    },
    '6': {
        defaultMessage: '6. Voldoende',
    },
    '5': {
        defaultMessage: '5. Matig',
    },
    '4': {
        defaultMessage: '4. Zeer matig',
    },
    '3': {
        defaultMessage: '3. Slecht',
    },
    '2': {
        defaultMessage: '2. Zeer slecht',
    },
    '1': {
        defaultMessage: '1. Verschikkelijk',
    },
});

export const getReviewOptions = (intl: IntlShape) =>
    Object.keys(reviewMessages)
        .reverse()
        .map(key => ({
            value: key,
            label: intl.formatMessage(reviewMessages[key as keyof typeof reviewMessages]),
        }));

const ReviewModal: FC<React.PropsWithChildren<ReviewModalProps>> = ({ data, ...props }) => {
    // used to see if we just left a review so we can show the "thank you for you review" part
    const normalizedData = normalizeReviewData(data);
    const [leftReview, setLeftReview] = useState(normalizedData?.review ? null : false);
    const item = normalizedData?.item;

    if (!normalizedData || !item) {
        return null;
    }

    return (
        <StyledModal {...props}>
            <ModalWrapper>
                <Content>
                    {typeof leftReview === 'boolean' && (
                        <>
                            {item.__typename === 'Booking' && (
                                <LeaveBookingReview
                                    booking={item}
                                    leftReview={leftReview}
                                    setLeftReview={setLeftReview}
                                />
                            )}
                            {item.__typename === 'Ticket' && (
                                <LeaveActivityReview
                                    ticket={item}
                                    activity={normalizedData.activity}
                                    leftReview={leftReview}
                                    setLeftReview={setLeftReview}
                                />
                            )}
                        </>
                    )}
                    {typeof leftReview !== 'boolean' && <ReadReview data={normalizedData} />}
                </Content>
            </ModalWrapper>
        </StyledModal>
    );
};

const StyledModal = styled(Modal)`
    ${wrapperPadding}
    && {
        padding: 0;
        display: flex;
        justify-content: center;
    }
`;

const ModalWrapper = styled(ContentWrapper)`
    flex-direction: column;
    display: flex;
    min-height: 90vh;
    width: 100%;
`;

const Content = styled(Box)`
    width: 100%;
    padding: ${({ theme }) => theme.spacing['50_Semi']} 0;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding: ${({ theme }) => theme.spacing['80_XXLarge']} 0;
    }
`;

export default ReviewModal;
